import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { foldJsonInMarkdown, promoteBoldLinesToHeadings } from '../../../lib/jsonFold';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Textarea } from '../../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/ui/dialog';
import {
  HouseViewBuilder, DEFAULT_BUILDER, composePhilosophyBrief,
} from '../../../components/committee/HouseViewBuilder';
import PageHeader from '../../../components/layout/PageHeader';
import { authHeaders } from '../../../lib/apiAuth';
import WorkspaceTabs from '../../../components/workspace/WorkspaceTabs';
import {
  Activity, AlertTriangle, BookOpen, Briefcase, CheckCircle2, Clock,
  Database, Download, FileText, GitBranch, Loader2, Play, Plus, RefreshCw,
  Settings, Shield, Sparkles, TrendingDown, TrendingUp, X,
} from 'lucide-react';
import {
  budgetBarColor, classForVerdict, commaListToArray, complianceLabel,
  eventColor, flushSSEBuffer, isValidIsin, normalizeIsin, summarizeCost,
} from '../../../lib/committeeHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/** Looks like user is typing an Indian ISIN — skip name search to avoid noise. */
function looksLikePartialIsin(q) {
  const s = (q || '').trim().toUpperCase();
  return /^INE[A-Z0-9]{0,11}$/.test(s) && s.length >= 3;
}

/**
 * Search company name / NSE symbol via india securities API; still accepts a pasted full ISIN.
 */
function IndiaSecuritySearchRow({
  onSubmit,
  submitLabel,
  busy,
  idleIcon: IdleIcon,
  initialIsin,
}) {
  const [query, setQuery] = useState(initialIsin && isValidIsin(normalizeIsin(initialIsin)) ? normalizeIsin(initialIsin) : '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(
    initialIsin && isValidIsin(normalizeIsin(initialIsin))
      ? { isin: normalizeIsin(initialIsin) }
      : null,
  );
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  // Re-seed the input when the parent (workspace tab) hands us a different ISIN.
  useEffect(() => {
    if (!initialIsin) return;
    const norm = normalizeIsin(initialIsin);
    if (!isValidIsin(norm)) return;
    setQuery(norm);
    setPicked({ isin: norm });
    setOpen(false);
    setResults([]);
  }, [initialIsin]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    const q = query.trim();
    const asIsin = normalizeIsin(q);
    if (isValidIsin(asIsin)) {
      setResults([]);
      setOpen(false);
      setSearching(false);
      return;
    }
    if (looksLikePartialIsin(q)) {
      setResults([]);
      setOpen(false);
      setSearching(false);
      return;
    }
    if (q.length < 2) {
      setResults([]);
      setOpen(false);
      setSearching(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axios.get(`${API}/india/securities`, {
          params: { search: q, page_size: 12, sort_by: 'company_name' },
        });
        const items = data.items || [];
        setResults(items);
        setOpen(items.length > 0);
      } catch {
        setResults([]);
        setOpen(false);
      } finally {
        setSearching(false);
      }
    }, 280);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const resolvedIsin = () => {
    if (picked?.isin && isValidIsin(normalizeIsin(picked.isin))) {
      return normalizeIsin(picked.isin);
    }
    const raw = normalizeIsin(query);
    return isValidIsin(raw) ? raw : null;
  };

  const handleSubmit = async () => {
    const isin = resolvedIsin();
    if (!isin) return;
    await onSubmit(isin);
    setQuery('');
    setPicked(null);
    setResults([]);
    setOpen(false);
  };

  const selectRow = (row) => {
    setPicked(row);
    setQuery(
      row.nse_symbol
        ? `${row.company_name} (${row.nse_symbol})`
        : row.company_name,
    );
    setOpen(false);
    setResults([]);
  };

  const canSubmit = !!resolvedIsin() && !busy;

  return (
    <div className="flex gap-2 flex-wrap items-start">
      <div className="relative flex-1 min-w-[200px]" ref={wrapRef}>
        <Input
          className={searching && !busy ? 'pr-9' : undefined}
          placeholder="Search company or symbol — or paste ISIN"
          value={query}
          disabled={busy}
          onChange={(e) => {
            setQuery(e.target.value);
            setPicked(null);
          }}
          onFocus={() => {
            if (results.length > 0) setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSubmit();
            }
          }}
        />
        {open && results.length > 0 && (
          <ul
            className="absolute z-50 mt-1 left-0 right-0 max-h-56 overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-md"
          >
            {results.map((row) => (
              <li key={row.isin}>
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => selectRow(row)}
                >
                  <div className="font-medium leading-snug">{row.company_name}</div>
                  <div className="text-xs text-muted-foreground font-mono">
                    {row.nse_symbol || '—'} · {row.isin}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
        {searching && !busy && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>
      <Button onClick={handleSubmit} disabled={!canSubmit}>
        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : (IdleIcon && <IdleIcon className="w-4 h-4 mr-1" />)}
        {submitLabel}
      </Button>
    </div>
  );
}

function VerdictBadge({ verdict, conviction }) {
  const cls = classForVerdict(verdict);
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border text-xs font-semibold ${cls}`}>
      {verdict}
      {typeof conviction === 'number' && (
        <span className="opacity-70">{conviction}/10</span>
      )}
    </span>
  );
}

function ComplianceBadge({ status }) {
  const label = complianceLabel(status);
  if (status === 'fail_blocked') {
    return <Badge variant="outline" className="border-red-500/30 text-red-500">{label}</Badge>;
  }
  if (status === 'fail_overridden') {
    return <Badge variant="outline" className="border-amber-500/30 text-amber-600">{label}</Badge>;
  }
  return <Badge variant="outline" className="border-emerald-500/30 text-emerald-500">{label}</Badge>;
}

// ── Coverage list editor ─────────────────────────────────────

function CoverageEditor() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/committee/coverage`);
      setItems(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async (isin) => {
    setAdding(true);
    try {
      await axios.post(`${API}/committee/coverage`, { isin });
      await load();
    } finally {
      setAdding(false);
    }
  };

  const remove = async (isin) => {
    await axios.delete(`${API}/committee/coverage/${isin}`);
    await load();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2"><Database className="w-4 h-4" /> Coverage List ({items.filter(i => i.active).length})</span>
          <Button variant="ghost" size="sm" onClick={load}><RefreshCw className="w-4 h-4" /></Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-4">
          <IndiaSecuritySearchRow
            onSubmit={add}
            submitLabel="Add"
            idleIcon={Plus}
            busy={adding}
          />
          <p className="text-xs text-muted-foreground mt-2">
            Type a company name or NSE symbol, pick from the list, or paste a 12-character ISIN.
          </p>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : (
          <div className="space-y-1 max-h-[420px] overflow-y-auto">
            {items.filter(i => i.active).map((it) => (
              <div key={it.id} className="flex items-center justify-between px-3 py-2 rounded border bg-card">
                <div>
                  <div className="font-medium">{it.nse_symbol || it.isin}</div>
                  <div className="text-xs text-muted-foreground">{it.company_name || it.isin}</div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => remove(it.isin)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
            {items.filter(i => i.active).length === 0 && (
              <div className="text-sm text-muted-foreground py-6 text-center">
                Coverage list is empty. Add a company (search by name) or ISIN to start nightly committee runs.
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Mandate editor ───────────────────────────────────────────

function MandateEditor() {
  const [mandate, setMandate] = useState({
    restricted_isins: [], sector_caps_jsonb: {},
    mcap_floor_inr_cr: '', max_position_pct: 5.0,
    house_view_md: '', monthly_budget_inr_paise: 5_000_000_000,
    per_run_budget_inr_paise: 150_000_000, sebi_categories_blocked: [],
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/committee/mandate`);
    if (data && data.exists) setMandate(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/committee/mandate`, mandate);
      await load();
    } finally { setSaving(false); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Shield className="w-4 h-4" /> Mandate</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <label className="text-xs text-muted-foreground">Restricted ISINs (comma-separated)</label>
          <Input value={(mandate.restricted_isins || []).join(', ')}
                 onChange={(e) => setMandate({...mandate,
                   restricted_isins: commaListToArray(e.target.value, { upper: true })})} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground">Mcap floor (₹ Cr)</label>
            <Input type="number" value={mandate.mcap_floor_inr_cr ?? ''}
                   onChange={(e) => setMandate({...mandate, mcap_floor_inr_cr: e.target.value ? parseFloat(e.target.value) : null})} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Max position (% NAV)</label>
            <Input type="number" step="0.1" value={mandate.max_position_pct}
                   onChange={(e) => setMandate({...mandate, max_position_pct: parseFloat(e.target.value || '0')})} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground">Per-run budget (₹)</label>
            <Input type="number" value={(mandate.per_run_budget_inr_paise || 0) / 100}
                   onChange={(e) => setMandate({...mandate, per_run_budget_inr_paise: Math.round(parseFloat(e.target.value || '0') * 100)})} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Monthly budget (₹)</label>
            <Input type="number" value={(mandate.monthly_budget_inr_paise || 0) / 100}
                   onChange={(e) => setMandate({...mandate, monthly_budget_inr_paise: Math.round(parseFloat(e.target.value || '0') * 100)})} />
          </div>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">SEBI categories blocked (comma-separated, e.g. ASM,GSM,T2T)</label>
          <Input value={(mandate.sebi_categories_blocked || []).join(', ')}
                 onChange={(e) => setMandate({...mandate,
                   sebi_categories_blocked: commaListToArray(e.target.value, { upper: true })})} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">House view (markdown)</label>
          <Textarea rows={6} value={mandate.house_view_md || ''}
                    onChange={(e) => setMandate({...mandate, house_view_md: e.target.value})} />
        </div>
        <Button onClick={save} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
          Save Mandate
        </Button>
      </CardContent>
    </Card>
  );
}

// ── On-demand run trigger ───────────────────────────────────

function OnDemandRunner({ onRunComplete, defaultIsin }) {
  const navigate = useNavigate();
  const [running, setRunning] = useState(false);
  const [events, setEvents] = useState([]);
  const [decision, setDecision] = useState(null);
  const [completedRunId, setCompletedRunId] = useState(null);

  const start = async (isinRaw) => {
    const isin = normalizeIsin(isinRaw || '');
    if (!isValidIsin(isin)) return;
    setRunning(true); setEvents([]); setDecision(null); setCompletedRunId(null);
    try {
      const url = `${API}/committee/run?stream=sse`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders(),
        },
        body: JSON.stringify({ isin }),
      });
      if (!res.body) throw new Error('SSE not supported');
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const flushed = flushSSEBuffer(buffer);
        buffer = flushed.remainder;
        for (const evt of flushed.events) {
          setEvents((evts) => [...evts, evt]);
          if (evt.type === 'decision.ready') setDecision(evt);
          if (evt.type === 'run.complete') {
            if (evt.run_id) setCompletedRunId(evt.run_id);
            if (onRunComplete) onRunComplete(evt);
            if (evt.run_id) navigate(`/public/committee/runs/${evt.run_id}`);
          }
        }
      }
    } catch (e) {
      console.error(e);
      setEvents((evts) => [...evts, { type: 'error', error: String(e) }]);
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Play className="w-4 h-4" /> On-Demand Committee Run</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <IndiaSecuritySearchRow
            onSubmit={start}
            submitLabel="Run Committee"
            idleIcon={Sparkles}
            busy={running}
            initialIsin={defaultIsin}
          />
          <p className="text-xs text-muted-foreground mt-2">
            Search by company name or symbol, or paste ISIN. The run still uses the security&apos;s ISIN in the API.
          </p>
        </div>
        {events.length > 0 && (
          <div className="border rounded p-3 max-h-[260px] overflow-y-auto bg-muted/30">
            <div className="text-xs text-muted-foreground mb-2">Live progress</div>
            {events.map((e, i) => (
              <div key={i} className="font-mono text-xs py-0.5">
                <span className={`inline-block w-32 ${eventColor(e.type)}`}>{e.type}</span>
                <span className="text-muted-foreground">{e.role || e.phase || ''}</span>
                {e.payload?.verdict && (
                  <span className="ml-2"><VerdictBadge verdict={e.payload.verdict} conviction={e.payload.conviction} /></span>
                )}
              </div>
            ))}
          </div>
        )}
        {decision && (
          <div className="border rounded p-4 bg-card flex items-center justify-between gap-3">
            <div>
              <div className="text-sm text-muted-foreground mb-1">Final Decision</div>
              <VerdictBadge verdict={decision.payload?.verdict} conviction={decision.payload?.conviction} />
            </div>
            {(completedRunId || decision.run_id) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/public/committee/runs/${completedRunId || decision.run_id}`)}
              >
                View full analysis
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Decisions table ──────────────────────────────────────────

function DecisionsTable({ refreshKey, filterIsin }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/committee/decisions?days=30&limit=50`);
      setRows(data || []);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const filtered = filterIsin
    ? rows.filter((r) => normalizeIsin(r.isin || '') === normalizeIsin(filterIsin))
    : rows;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Briefcase className="w-4 h-4" />
            Recent Decisions
            {filterIsin && (
              <Badge variant="outline" className="ml-2 font-mono text-[10px]">
                filtered to {normalizeIsin(filterIsin)}
              </Badge>
            )}
          </span>
          <Button variant="ghost" size="sm" onClick={load}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {filtered.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">
            {filterIsin
              ? <>No prior decisions for <code className="font-mono">{normalizeIsin(filterIsin)}</code> in the last 30 days. Run the committee above to create one.</>
              : 'No committee decisions yet. Trigger an on-demand run or wait for the nightly batch.'}
          </div>
        ) : (
          <div className="space-y-1">
            {filtered.map((r) => (
              <div key={r.id}
                   className="flex items-center justify-between px-3 py-2 rounded border bg-card hover:border-blue-500/40 cursor-pointer"
                   onClick={() => navigate(`/public/committee/runs/${r.run_id}`)}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <VerdictBadge verdict={r.verdict} conviction={r.conviction} />
                  <div className="min-w-0">
                    <div className="font-medium truncate">{r.nse_symbol || r.company_name || r.isin}</div>
                    {(r.company_name || r.nse_symbol) && (
                      <div className="text-[11px] text-muted-foreground truncate">
                        {r.company_name || r.nse_symbol}
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground truncate">{r.one_line_thesis}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {r.view_changed && (
                    <Badge variant="outline" className="border-amber-500/30 text-amber-600">View Δ</Badge>
                  )}
                  <ComplianceBadge status={r.compliance_status} />
                  <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Cost panel ───────────────────────────────────────────────

function CostPanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/committee/cost?days=30`).then((r) => setData(r.data)).catch(() => {});
  }, []);
  const summary = summarizeCost(data);
  if (!summary) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Activity className="w-4 h-4" /> Monthly Cost</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">₹{summary.used.toLocaleString('en-IN', {maximumFractionDigits: 0})}</div>
        <div className="text-xs text-muted-foreground">of ₹{summary.budget.toLocaleString('en-IN', {maximumFractionDigits: 0})} ({summary.pct.toFixed(1)}%)</div>
        <div className="w-full h-2 bg-muted rounded mt-2 overflow-hidden">
          <div className={`h-full ${summary.barColor}`} style={{ width: `${Math.min(100, summary.pct)}%` }} />
        </div>
        <div className="text-xs text-muted-foreground mt-3">{summary.runs} runs this month</div>
      </CardContent>
    </Card>
  );
}

// ── Morning brief ────────────────────────────────────────────

function MorningBriefPanel() {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(false);
  const fetchBrief = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/committee/morning-brief?hours=24`);
      setBrief(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchBrief(); }, []);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2"><BookOpen className="w-4 h-4" /> Morning Brief</span>
          <Button variant="ghost" size="sm" onClick={fetchBrief}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!brief ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : (
          <>
            <div className="text-xs text-muted-foreground mb-3">
              {brief.decisions_count} decisions • {brief.view_changes_count} view-changes
            </div>
            <div className="prose prose-sm dark:prose-invert max-w-none break-words bg-muted/30 p-3 rounded max-h-[400px] overflow-y-auto
                            prose-headings:mt-3 prose-headings:mb-2
                            prose-h1:text-base prose-h2:text-sm prose-h3:text-sm
                            prose-p:my-1.5 prose-ul:my-1.5 prose-li:my-0.5
                            prose-strong:text-foreground
                            prose-pre:max-w-full prose-pre:overflow-x-auto prose-pre:whitespace-pre-wrap
                            prose-pre:break-words prose-pre:text-xs">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {promoteBoldLinesToHeadings(foldJsonInMarkdown(brief.brief_md || ''))}
              </ReactMarkdown>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── Research tiers (three-tier platform demo) ────────────────

// "New desk" wizard: name + AI-drafted house view + name picker → creates
// the institutional desk in-app and live-polls its overlay runs.
function NewDeskDialog({ open, onOpenChange, onCreated }) {
  const navigate = useNavigate();
  const [step, setStep] = useState('form'); // form | progress
  const [name, setName] = useState('');
  const [builder, setBuilder] = useState(DEFAULT_BUILDER());
  const [houseView, setHouseView] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [corpus, setCorpus] = useState([]);
  const [selected, setSelected] = useState({});
  const [desk, setDesk] = useState(null);
  const [overlays, setOverlays] = useState([]);

  // Platform corpus for the stock picker (only overlayable names matter,
  // but the latest-decision list is a good proxy and loads instantly).
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/committee/platform/decisions?limit=200`)
      .then((r) => setCorpus(r.data || []))
      .catch(() => setCorpus([]));
  }, [open]);

  // Poll overlay progress after creation.
  useEffect(() => {
    if (step !== 'progress' || !desk?.tenant_id) return undefined;
    const poll = () => {
      axios.get(`${API}/committee/desks/${desk.tenant_id}/overlays`)
        .then((r) => setOverlays(r.data || []))
        .catch(() => {});
    };
    poll();
    const id = setInterval(poll, 5000);
    return () => clearInterval(id);
  }, [step, desk]);

  const reset = () => {
    setStep('form'); setName(''); setHouseView('');
    setBuilder(DEFAULT_BUILDER());
    setSelected({}); setDesk(null); setOverlays([]); setError(null);
  };

  const draftWithAI = async () => {
    if (drafting) return;
    const brief = composePhilosophyBrief(builder);
    if (!brief) return;
    setDrafting(true); setError(null);
    try {
      const { data } = await axios.post(`${API}/committee/desks/draft_house_view`, { brief });
      setHouseView(data.house_view_md || '');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Draft failed');
    } finally {
      setDrafting(false);
    }
  };

  const create = async () => {
    const symbols = Object.keys(selected).filter((s) => selected[s]);
    if (!name.trim() || houseView.trim().length < 40 || creating) return;
    setCreating(true); setError(null);
    try {
      const { data } = await axios.post(`${API}/committee/desks`, {
        name: name.trim(),
        house_view_md: houseView,
        symbols,
        run_overlays: true,
        builder,
      });
      setDesk(data);
      setStep('progress');
      if (onCreated) onCreated();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Desk creation failed');
    } finally {
      setCreating(false);
    }
  };

  const pickedCount = Object.values(selected).filter(Boolean).length;
  const doneCount = overlays.filter((o) => o.status === 'completed').length;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        {step === 'form' ? (
          <>
            <DialogHeader>
              <DialogTitle>New house-view desk</DialogTitle>
              <DialogDescription>
                Same shared research, your verdicts. Describe the philosophy,
                pick names, and the committee re-judges the evidence under
                this mandate (~₹80–150 per name).
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder='Desk name — e.g. "Prospect Capital"'
              />

              {/* Philosophy builder — dropdowns compose the brief for the AI drafter */}
              <div className="rounded-md border border-border p-3 space-y-2.5">
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Investment philosophy
                </div>
                <HouseViewBuilder value={builder} onChange={setBuilder} />
                <Button size="sm" variant="outline" onClick={draftWithAI} disabled={drafting}>
                  {drafting
                    ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                    : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                  Draft house view with AI
                </Button>
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  Every rule here is yours to change — including after the desk
                  is created. Edit the drafted markdown directly below, or
                  reopen this builder later from any overlay run page.
                </p>
              </div>
              <Textarea
                value={houseView}
                onChange={(e) => setHouseView(e.target.value)}
                rows={10}
                className="font-mono text-xs"
                placeholder="House view (markdown). Draft it with AI above, or write hard rules directly: valuation ceilings, horizon, exclusions, sizing…"
              />
              <div>
                <div className="text-xs text-muted-foreground mb-1.5">
                  Names to judge under this view ({pickedCount} selected
                  {pickedCount > 0 ? ` · est. ₹${pickedCount * 80}–${pickedCount * 150}` : ''})
                </div>
                <div className="max-h-36 overflow-y-auto rounded-md border border-border p-2 grid grid-cols-3 gap-1">
                  {corpus.map((c) => (
                    <label key={c.isin}
                           className="flex items-center gap-1.5 text-xs cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5">
                      <input
                        type="checkbox"
                        checked={!!selected[c.nse_symbol]}
                        onChange={(e) => setSelected((s) => ({ ...s, [c.nse_symbol]: e.target.checked }))}
                      />
                      <span className="truncate">{c.nse_symbol}</span>
                      <span className={`ml-auto text-[10px] ${classForVerdict(c.verdict)} px-1 rounded`}>{c.verdict}</span>
                    </label>
                  ))}
                  {corpus.length === 0 && (
                    <div className="col-span-3 text-xs text-muted-foreground p-2">
                      No platform decisions available yet.
                    </div>
                  )}
                </div>
              </div>
              {error && <div className="text-xs text-red-500">{error}</div>}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={create}
                      disabled={creating || !name.trim() || houseView.trim().length < 40 || pickedCount === 0}>
                {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
                Create desk & run {pickedCount || ''} overlay{pickedCount === 1 ? '' : 's'}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{desk?.name} — judging under the house view</DialogTitle>
              <DialogDescription>
                {doneCount}/{desk?.queued?.length || 0} names judged · each takes
                ~1–2 min. Leave this open or come back via the Research tiers panel.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-1.5">
              {(desk?.queued || []).map((sym) => {
                const ov = overlays.find((o) => o.nse_symbol === sym);
                return (
                  <div key={sym} className="flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5 text-sm">
                    <span className="font-medium w-28 truncate">{sym}</span>
                    {!ov || ov.status === 'running' || ov.status === 'pending' ? (
                      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> committee deliberating…
                      </span>
                    ) : ov.status === 'completed' ? (
                      <>
                        <Badge variant="outline" className={classForVerdict(ov.verdict)}>
                          {ov.verdict} {ov.conviction}/10
                        </Badge>
                        <span className="text-xs text-muted-foreground truncate flex-1" title={ov.one_line_thesis}>
                          {ov.one_line_thesis}
                        </span>
                        <button className="text-xs text-primary hover:underline whitespace-nowrap"
                                onClick={() => navigate(`/public/committee/runs/${ov.run_id}`)}>
                          Open →
                        </button>
                      </>
                    ) : (
                      <span className="text-xs text-red-500">{ov.status}{ov.error ? ` — ${ov.error.slice(0, 60)}` : ''}</span>
                    )}
                  </div>
                );
              })}
              {(desk?.skipped || []).map((sym) => (
                <div key={sym} className="flex items-center gap-2 rounded-md border border-dashed border-border px-2.5 py-1.5 text-xs text-muted-foreground">
                  <span className="font-medium w-28">{sym}</span>
                  not in the platform corpus yet — run the full committee on it first
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ResearchTiersPanel() {
  const navigate = useNavigate();
  const [tiers, setTiers] = useState(null);
  const [newDeskOpen, setNewDeskOpen] = useState(false);

  const loadTiers = useCallback(() => {
    axios.get(`${API}/committee/tiers`)
      .then((r) => setTiers(r.data))
      .catch(() => setTiers(null));
  }, []);

  useEffect(() => { loadTiers(); }, [loadTiers]);

  if (!tiers || !tiers.platform) return null;

  const TierRow = ({ badge, title, desc, action, onClick }) => (
    <div className="rounded-md border border-border p-2.5 space-y-1">
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="text-[10px] uppercase">{badge}</Badge>
        <span className="text-sm font-medium">{title}</span>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>
      {action && (
        <button onClick={onClick} className="text-xs text-primary hover:underline">
          {action} →
        </button>
      )}
    </div>
  );

  // Prefer a desk whose latest overlay the current user can actually open
  // (i.e. one they're a member of); fall back to any desk for the blurb.
  const topDesk = (tiers.desks || []).find((d) => d.latest_run_id)
    || (tiers.desks || []).find((d) => d.overlays > 0)
    || (tiers.desks || [])[0];

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <GitBranch className="w-4 h-4" /> Research tiers
          </span>
          <Button size="sm" variant="outline" className="h-7 text-xs"
                  onClick={() => setNewDeskOpen(true)}>
            <Plus className="w-3.5 h-3.5 mr-1" /> New desk
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <TierRow
          badge="Base"
          title="Shared platform research"
          desc={`${tiers.platform.name} underwrites the universe once — ${tiers.decisions} standing decisions across ${tiers.coverage} covered names. Every client portfolio is built on this corpus.`}
          action="Open portfolio built on platform decisions"
          onClick={() => navigate('/public/committee/portfolio')}
        />
        <TierRow
          badge="Plus"
          title="On-demand runs + watchlists"
          desc="Run the full committee on any name outside the shared corpus, metered by credits. Use the runner on this page — the live run streams every agent."
        />
        <TierRow
          badge="Insti"
          title="House-view overlay desks"
          desc={
            topDesk
              ? `${tiers.desks.length} desk${tiers.desks.length > 1 ? 's' : ''} re-judge the shared evidence under their own mandate. ${topDesk.name}: ${topDesk.overlays} overlay verdict${topDesk.overlays === 1 ? '' : 's'} on file.`
              : 'Institutions re-judge shared evidence under their own mandate — same analysts, different verdicts.'
          }
          action={topDesk?.latest_run_id ? 'Open latest house-view overlay' : null}
          onClick={() => topDesk?.latest_run_id && navigate(`/public/committee/runs/${topDesk.latest_run_id}`)}
        />
      </CardContent>
      <NewDeskDialog
        open={newDeskOpen}
        onOpenChange={setNewDeskOpen}
        onCreated={loadTiers}
      />
    </Card>
  );
}

// ── Page entry ───────────────────────────────────────────────

export default function CommitteePage() {
  const [refreshKey, setRefreshKey] = useState(0);
  const onRunComplete = () => setRefreshKey((k) => k + 1);

  // "Open workspace tab" dialog — replaces window.prompt. onNewTab returns a
  // promise resolved by the dialog's submit/cancel handlers.
  const [newTabOpen, setNewTabOpen] = useState(false);
  const [newTabIsin, setNewTabIsin] = useState('');
  const newTabResolver = useRef(null);

  const resolveNewTab = useCallback((value) => {
    setNewTabOpen(false);
    const resolver = newTabResolver.current;
    newTabResolver.current = null;
    if (resolver) resolver(value);
  }, []);

  const submitNewTab = useCallback(() => {
    const trimmed = (newTabIsin || '').trim().toUpperCase();
    if (!trimmed) { resolveNewTab(null); return; }
    const isin = normalizeIsin(trimmed);
    const valid = isValidIsin(isin);
    resolveNewTab({
      id: `ticker-${valid ? isin : trimmed}`,
      label: valid ? isin : trimmed,
      subtitle: 'focus',
      payload: { ticker: trimmed, isin: valid ? isin : null },
    });
  }, [newTabIsin, resolveNewTab]);

  const renderTab = (tab) => {
    if (tab.id === 'control') {
      return (
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-4">
              <OnDemandRunner onRunComplete={onRunComplete} />
              <DecisionsTable refreshKey={refreshKey} />
            </div>
            <div className="space-y-4">
              <ResearchTiersPanel />
              <CostPanel />
              <MorningBriefPanel />
              <CoverageEditor />
              <MandateEditor />
            </div>
          </div>
        </div>
      );
    }
    // Ticker-focus tab — narrower workspace for one name at a time.
    const focusIsin = tab.payload?.isin || (isValidIsin(normalizeIsin(tab.payload?.ticker || '')) ? normalizeIsin(tab.payload.ticker) : null);
    return (
      <div className="p-4 space-y-4">
        <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
          Workspace focus: <span className="font-semibold text-foreground">{tab.payload?.ticker || tab.label}</span>
          {focusIsin ? (
            <span className="text-muted-foreground"> — runner is pre-filled with <code className="font-mono text-foreground">{focusIsin}</code>; decisions below are filtered to this ISIN.</span>
          ) : (
            <span className="text-muted-foreground"> — paste an ISIN to focus, or use the runner below.</span>
          )}
        </div>
        <OnDemandRunner onRunComplete={onRunComplete} defaultIsin={focusIsin} />
        <DecisionsTable refreshKey={refreshKey} filterIsin={focusIsin} />
      </div>
    );
  };

  const onNewTab = () => new Promise((resolve) => {
    newTabResolver.current = resolve;
    setNewTabIsin('');
    setNewTabOpen(true);
  });

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="CIO Committee"
        subtitle="Multi-agent investment committee — analysts, debate, risk panel, compliance, CIO."
      />
      <div className="flex-1 min-h-0 mt-2 border border-border rounded-md overflow-hidden">
        <WorkspaceTabs
          storageKey="kubair.workspace.committee"
          defaultTab={{ id: 'control', label: 'Control center', subtitle: 'all books', sticky: true }}
          renderContent={renderTab}
          onNewTab={onNewTab}
        />
      </div>

      <Dialog
        open={newTabOpen}
        onOpenChange={(open) => { if (!open) resolveNewTab(null); }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Open workspace tab</DialogTitle>
            <DialogDescription>
              Paste an ISIN to focus the runner and decisions on a single name.
            </DialogDescription>
          </DialogHeader>
          <Input
            autoFocus
            value={newTabIsin}
            onChange={(e) => setNewTabIsin(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submitNewTab(); }}
            placeholder="e.g. INE002A01018 (RELIANCE)"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => resolveNewTab(null)}>Cancel</Button>
            <Button onClick={submitNewTab} disabled={!newTabIsin.trim()}>Open tab</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
